import { getPreferenceEntry, type PreferenceEntry, type PreferenceKey, type PreferenceValue } from './registry'

export const STORAGE_PREFIX = 'pref:'

type Listener = () => void
const listeners = new Set<Listener>()
let storageBound = false

function storageKey(key: PreferenceKey): string {
  return STORAGE_PREFIX + key
}

function readRaw(key: PreferenceKey): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage?.getItem(storageKey(key)) ?? null
  } catch {
    return null
  }
}

function coerce(entry: PreferenceEntry, raw: string): PreferenceValue {
  if (entry.type === 'boolean') return raw === 'true'
  if (entry.type === 'enum') {
    return entry.options?.some((o) => o.value === raw) ? raw : entry.default
  }
  return raw // route
}

// Reads fall back to the registry default. Account-scoped keys are inert this
// slice — always their default, never touching storage.
export function getValue(key: PreferenceKey): PreferenceValue {
  const entry = getPreferenceEntry(key)
  if (entry.scope !== 'device') return entry.default
  const raw = readRaw(key)
  if (raw === null) return entry.default
  return coerce(entry, raw)
}

export function setValue(key: PreferenceKey, value: PreferenceValue): void {
  const entry = getPreferenceEntry(key)
  if (entry.scope !== 'device') return // account-scoped: no-op this slice
  if (entry.type === 'enum' && !entry.options?.some((o) => o.value === value)) return // reject invalid
  if (typeof window === 'undefined') return
  try {
    if (!window.localStorage) return
    window.localStorage.setItem(storageKey(key), String(value))
  } catch {
    return // quota / privacy mode — silently ignore, UI keeps working off the default
  }
  notify()
}

function notify(): void {
  listeners.forEach((l) => l())
}

function handleStorage(e: StorageEvent): void {
  // key === null when localStorage.clear() fires; otherwise only react to our namespace.
  if (e.key === null || e.key.startsWith(STORAGE_PREFIX)) notify()
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  if (!storageBound && typeof window !== 'undefined') {
    window.addEventListener('storage', handleStorage)
    storageBound = true
  }
  return () => {
    listeners.delete(listener)
  }
}
